import React from 'react';
import { CheckIcon, MoonStarIcon, ShieldCheckIcon, SparklesIcon, ZapIcon } from 'lucide-react';
import { CtaButton } from '../components/CtaButton';
import { FaqItem } from '../components/FaqItem';
import { bonuses, faqs, features, heroIncludes, offerChecklist, outcomes, painPoints, volumeTitles } from '../data/content';
const EBOOK_COVER_URL = 'https://cdn.magicpatterns.com/uploads/6bVWAGwAHJ7VNCabcqAiXr/Mockup_(1).png';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/1roViPmDXjaiz1sGkRNVUW/Logo_IG.jpg';
const phases = [{
  label: 'Intake',
  description: 'Forms and session flow that get you ready in 15 minutes.'
}, {
  label: 'Interpret',
  description: 'Sun, Moon, and Rising templates for every scenario.'
}, {
  label: 'Deepen',
  description: 'Money and relationships read with context, not generalities.'
}, {
  label: 'Predict',
  description: 'Transits and returns with clear timing frameworks.'
}, {
  label: 'Relate',
  description: 'Synastry and composite charts with ready-made scripts.'
}, {
  label: 'Guide',
  description: 'Vocation and purpose mapped into a concrete roadmap.'
}];
export function SalesPage() {
  return <div className="min-h-screen w-full overflow-x-hidden bg-brand-cream font-body text-slate-800" data-id="element-10" data-mp="35qx35zi26osm">

      {/* Top banner */}
      <header data-id="element-11" data-mp="2vutzgp7k6i3k">
        <div className="bg-brand-medium px-4 py-2 text-center text-xs font-bold tracking-wide text-white sm:text-sm" data-id="element-12" data-mp="2vvc6mgvregq9">
          <span aria-hidden="true" data-id="element-13" data-mp="3thjcrv7poh4q">✨ </span>Digital download — Instant access after purchase
        </div>
        
      </header>

      <main id="top" data-id="element-14" data-mp="3okapjg7phmwm">

        {/* Hero */}
        <section className="bg-brand-deep px-5 py-8 text-center text-white sm:py-14 lg:py-16" data-id="element-15" data-mp="3jjvfoymmuxje">
          <div className="mx-auto max-w-4xl" data-id="element-16" data-mp="3fj32xvejyaid">
            <h1 className="font-display text-4xl font-bold leading-[1.08] tracking-tight sm:text-5xl lg:text-7xl" data-id="element-17" data-mp="3fi9t2lkar4w0">
              Stop Improvising. Start Leading as a{' '}
              <span className="text-brand-gold" data-id="element-18" data-mp="243ef71th57gi">Professional Astrologer</span>
            </h1>
            <p className="mx-auto mt-3 max-w-2xl text-base leading-relaxed text-white/85 sm:mt-4 sm:text-xl" data-id="element-19" data-mp="25kjxqqsftm3x">
              Stop giving readings, start leading consultations. A full{' '}
              <strong className="font-black text-white" data-id="element-20" data-mp="1vn5fclmyen8z">300-page system</strong> of professional scripts to scale your practice with zero preparation stress.
            </p>
            <img src={EBOOK_COVER_URL} alt="Astrologer Ready — the complete 6-volume system plus 5 bonuses included" className="mx-auto mt-3 w-full max-w-[280px] bg-transparent object-contain sm:mt-5 sm:max-w-md lg:max-w-lg" data-id="element-21" data-mp="20njqicjuqdb8" />
            <div className="mt-4 sm:mt-6" data-id="element-22" data-mp="1cwo0nkyyhs6m">
              <CtaButton label="YES, I WANT ASTROLOGER READY!" data-id="element-23" data-mp="1cxpbboc28cnt" />
              <p className="mt-3 text-xs font-bold tracking-wide text-white/70" data-id="element-24" data-mp="1hufuwln2pqdz">
                Secure checkout in your local currency
              </p>
            </div>

            {/* What you get */}
            <div className="mx-auto mt-8 max-w-2xl text-left sm:mt-10" data-id="element-25" data-mp="17w00a8ou7yzq">
              <p className="text-center text-sm font-black uppercase tracking-[0.16em] text-brand-gold" data-id="element-26" data-mp="p8he1gsvqd21">
                Here is everything you get
              </p>
              <ul className="mt-4 grid gap-2 sm:grid-cols-2" role="list" data-id="element-27" data-mp="kbr132cdfxku">
                {heroIncludes.map(item => <li key={item} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3" data-id="element-28" data-mp="y7cmz0nb6191">
                    <CheckIcon className="mt-0.5 h-5 w-5 flex-none text-brand-gold" aria-hidden="true" data-id="element-29" data-mp="187d8mt8ngtge" />
                    <span className="text-[15px] leading-snug text-white/90" data-id="element-30" data-mp="138s4wju6vrn7">{item}</span>
                  </li>)}
              </ul>

              <p className="mt-7 text-center text-sm font-black uppercase tracking-[0.16em] text-brand-gold" data-id="element-31" data-mp="1gyvqd8l8vhbl">
                The 6 volumes inside
              </p>
              <ul className="mt-4 space-y-2" role="list" data-id="element-32" data-mp="1qxux2lmg92vy">
                {volumeTitles.map(volume => <li key={volume} className="flex items-start gap-3 border-l-2 border-brand-gold/60 pl-4 text-[15px] leading-snug text-white/90" data-id="element-33" data-mp="1lz1kcvezlfwu">
                    {volume}
                  </li>)}
              </ul>
            </div>
          </div>
        </section>

        {/* Social proof bar */}
        <section aria-label="Social proof" className="bg-[#E6DCF3] px-5 py-4 text-center text-sm font-bold sm:text-base text-brand-deep" data-id="element-34" data-mp="2etg4rux227zj">
          <span aria-hidden="true" data-id="element-35" data-mp="2jrhmabeu1z8w">🔮 </span>Built for astrologers who want to charge like professionals, not improvise every session
        </section>

        {/* Pain points */}
        <section id="pain-points" className="bg-brand-cream px-5 py-12 sm:py-16" data-id="element-36" data-mp="1r3c0rmfyg680">
          <div className="mx-auto max-w-5xl" data-id="element-37" data-mp="1w2ygc2j99yjz">
            <div className="mx-auto max-w-2xl text-center" data-id="element-38" data-mp="1w26w1nv3zn90">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-39" data-mp="211rwy3tua5od">
                You are not alone
              </p>
              <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-40" data-mp="37hlpiho3zwzd">
                Does any of this sound familiar?
              </h2>
            </div>
            <ul className="mt-8 grid gap-4 sm:grid-cols-2 sm:gap-5" role="list" data-id="element-41" data-mp="10tbnv8a57bch">
              {painPoints.map(point => <li key={point.text} className="flex items-start gap-4 rounded-2xl border border-brand-medium/15 bg-white p-5 shadow-sm" data-id="element-42" data-mp="vvjwcfdetr8t">
                  <span className="mt-0.5 text-2xl" aria-hidden="true" data-id="element-43" data-mp="m58fxtcjhpl1">{point.emoji}</span>
                  <span className="text-base leading-relaxed text-slate-700" data-id="element-44" data-mp="i3dfer2fng8b">{point.text}</span>
                </li>)}
            </ul>
            <blockquote className="mx-auto mt-10 max-w-3xl border-l-4 border-brand-medium pl-5 font-display text-xl font-bold italic leading-relaxed text-brand-deep sm:text-2xl" data-id="element-45" data-mp="d40sk6bka95q">
              "You do not have an astrology problem. You are missing the operating system for your practice."
            </blockquote>
            <p className="mx-auto mt-5 max-w-3xl text-center text-base leading-relaxed text-slate-600" data-id="element-46" data-mp="22hx9qxugvve0">
              Most astrologers prepare every session from scratch, improvise the close, and rely on memory to interpret. That is not depth — those are unbilled hours. What separates a profitable practice from a second job is having the process written down once.
            </p>
          </div>
        </section>

        {/* Solution */}
        <section id="solution" className="bg-brand-deep px-5 py-12 text-white sm:py-16" data-id="element-47" data-mp="3i0bmkkjslhwz">
          <div className="mx-auto max-w-3xl text-center" data-id="element-48" data-mp="3mz6goin7mswe">
            <MoonStarIcon className="mx-auto h-10 w-10 text-brand-gold" aria-hidden="true" data-id="element-49" data-mp="emhazk0unbez" />
            <p className="mt-5 text-sm font-black uppercase tracking-[0.16em] text-brand-gold" data-id="element-50" data-mp="jksmdz6sltzb">The solution</p>
            <h2 className="mt-3 font-display text-4xl font-bold leading-tight tracking-tight sm:text-5xl" data-id="element-51" data-mp="5jpzxlbvyhnv">
              You don&apos;t need to work more hours. You need a documented system.
            </h2>
            <div className="mt-5 space-y-4 text-lg leading-relaxed text-white/85" data-id="element-52" data-mp="2qa73qqbol2xv">
              <p data-id="element-53" data-mp="2udn0dmnr33ak">
                Astrologer Ready is not another astrology course. It is a 6-volume operating manual for your practice: the forms that structure the consultation, the interpretations you no longer have to write, and the scripts that carry every difficult conversation.
              </p>
              <p data-id="element-54" data-mp="2wq7a5vngxwc9">
                <strong className="font-bold text-white" data-id="element-55" data-mp="2wqqn0xy9hujf">Set it up once</strong> and every future client gets the same professional experience — without you losing another afternoon preparing a chart.
              </p>
            </div>
            <div className="mt-8 grid gap-3 text-left sm:grid-cols-2" data-id="element-56" data-mp="1z3i5fe1dt5mp">
              {phases.map(phase => <div key={phase.label} className="rounded-xl border border-white/15 bg-white/5 p-4" data-id="element-57" data-mp="1z1ni5t2n11fg">
                  <p className="text-sm font-black uppercase tracking-wide text-brand-gold" data-id="element-58" data-mp="291o4zozptzoe">{phase.label}</p>
                  <p className="mt-1 text-sm leading-relaxed text-white/80" data-id="element-59" data-mp="1lbttbar4uh5q">{phase.description}</p>
                </div>)}
            </div>
          </div>
        </section>

        {/* What's inside */}
        <section id="inside" className="bg-[#EADFF5] px-5 py-12 sm:py-16" data-id="element-60" data-mp="utxidu3zq7yu">
          <div className="mx-auto max-w-6xl" data-id="element-61" data-mp="uvs2553izu1c">
            <div className="mx-auto max-w-2xl text-center" data-id="element-62" data-mp="zrz3ekkz8hcy">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-63" data-mp="zulh71ygnt3m">
                Fill-in-the-blank templates, ready to use
              </p>
              <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-64" data-mp="18urtwlmc4p1q">
                The 6 Volumes of the System
              </h2>
            </div>
            <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-id="element-65" data-mp="1iq1vj88tmuez">
              {features.map((feature, index) => <article key={feature.title} className="rounded-2xl border border-brand-medium/15 bg-white p-5 shadow-sm" data-id="element-66" data-mp="1nj4r1lx5sdxt" data-mpi={index}>
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand-medium font-display text-lg font-bold text-white" data-id="element-67" data-mp="1sj922wfasggn">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <h3 className="mt-5 font-display text-xl font-bold leading-snug text-brand-deep" data-id="element-68" data-mp="3m17xt0yvw439">
                    {feature.title}
                  </h3>
                  <p className="mt-2 text-xs font-black uppercase tracking-wide text-brand-medium" data-id="element-69" data-mp="3czgt9zg2y3az">
                    {feature.pain}
                  </p>
                  <p className="mt-3 text-[15px] leading-relaxed text-slate-600" data-id="element-70" data-mp="381ozezvvk745">
                    {feature.description}
                  </p>
                </article>)}
            </div>
          </div>
        </section>

        {/* Bonuses */}
        <section id="bonuses" className="bg-[#F3EDFB] px-5 py-12 sm:py-16" data-id="element-71" data-mp="3vycl544gu5ln">
          <div className="mx-auto max-w-5xl" data-id="element-72" data-mp="3vw89g00bs2at">
            <div className="mx-auto max-w-2xl text-center" data-id="element-73" data-mp="3r01cuxttk35j">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-74" data-mp="3qxf5ay6xhnnb">
                Included at no extra cost
              </p>
              <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-75" data-mp="nhhqud1qhtj6">
                🎁 5 Exclusive Bonuses Included
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-slate-700" data-id="element-76" data-mp="2wfolj1hjpwlv">
                Everything you need to deliver a professional-grade consultation starting today.
              </p>
            </div>
            <div className="mt-8 grid gap-4 md:grid-cols-2" data-id="element-77" data-mp="36d32d27dsnep">
              {bonuses.map(bonus => <article key={bonus.title} className="rounded-r-2xl border-l-4 border-brand-medium bg-white px-5 py-5 shadow-sm sm:px-6" data-id="element-78" data-mp="3kcua4l7b17k9">
                  <div className="flex items-start gap-4" data-id="element-79" data-mp="3fd7w49jnjnae">
                    <span className="text-3xl" aria-hidden="true" data-id="element-80" data-mp="3u3f8c7j2ga2n">{bonus.emoji}</span>
                    <div data-id="element-81" data-mp="3p3u71qgfbpmr">
                      <span className="inline-block rounded-full bg-brand-gold px-3 py-1 text-xs font-black uppercase tracking-wide text-brand-deep" data-id="element-82" data-mp="3p3kim7b11qj6">
                        {bonus.tag}
                      </span>
                      <h3 className="mt-3 font-display text-xl font-bold leading-snug text-brand-deep" data-id="element-83" data-mp="20fprocowr0y">
                        {bonus.title}
                      </h3>
                      <p className="mt-2 text-[15px] leading-relaxed text-slate-600" data-id="element-84" data-mp="qkj3rk9b1ix0">
                        {bonus.description}
                      </p>
                    </div>
                  </div>
                </article>)}
            </div>
          </div>
        </section>

        {/* Author */}
        <section id="author" className="bg-brand-cream px-5 py-12 sm:py-16" data-id="element-85" data-mp="2twu5ojnjqkw">
          <div className="mx-auto flex max-w-4xl flex-col items-center gap-7 text-center md:flex-row md:text-left" data-id="element-86" data-mp="7l54cmst3fz4">
            <img src={LOGO_URL} alt="Prof. Kyra logo" className="h-40 w-40 flex-none rounded-full border-8 border-white bg-black object-contain shadow-lg" data-id="element-87" data-mp="3p256tq8k2ugt" />
            <div data-id="element-88" data-mp="3u0zqeueckn1z">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-89" data-mp="3tydjmwza6bkt">
                Created with care
              </p>
              <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-90" data-mp="3kz053pwauwt8">
                Meet Prof. Kyra
              </h2>
              <p className="mt-5 text-lg leading-relaxed text-slate-700" data-id="element-91" data-mp="1ltcy21xiyoxz">
                Prof. Kyra creates practical, field-tested digital guides designed to solve real problems. Every resource is built to hand you concrete tools — not just information.
              </p>
              <p className="mt-4 text-lg leading-relaxed text-slate-700" data-id="element-92" data-mp="1cqbti5ob55wf">
                Astrologer Ready was created for astrologers who want an organized, profitable, professional practice instead of entire afternoons spent preparing a single chart.
              </p>
            </div>
          </div>
        </section>

        {/* Outcomes */}
        <section id="outcomes" className="bg-[#EADFF5] px-5 py-12 sm:py-16" data-id="element-93" data-mp="1vjpgnols481h">
          <div className="mx-auto max-w-4xl text-center" data-id="element-94" data-mp="p2kgezuy0ou3">
            <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-95" data-mp="p45bcql669sh">
              What you will achieve
            </p>
            <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-96" data-mp="2vrcp80x4193o">
              After the System
            </h2>
            <p className="mx-auto mt-5 max-w-3xl font-display text-xl font-bold italic leading-relaxed text-brand-deep" data-id="element-97" data-mp="30qfhslvwlmur">
              "A great reading is not improvised. It is the result of a system written down once."
            </p>
            <ul className="mt-8 grid gap-4 text-left sm:grid-cols-2" role="list" data-id="element-98" data-mp="3ehue1uetnkjr">
              {outcomes.map(outcome => <li key={outcome} className="flex items-start gap-3 rounded-2xl border border-brand-medium/15 bg-white p-5 shadow-sm" data-id="element-99" data-mp="3jg5oa6913x2i">
                  <CheckIcon className="mt-0.5 h-5 w-5 flex-none text-brand-medium" aria-hidden="true" data-id="element-100" data-mp="3oh3266n0a4y7" />
                  <span className="text-base leading-relaxed text-slate-700" data-id="element-101" data-mp="3sgtsk98mej27">{outcome}</span>
                </li>)}
            </ul>
          </div>
        </section>

        {/* Offer */}
        <section id="offer" className="bg-brand-deep px-5 py-12 sm:py-16" data-id="element-102" data-mp="1y4dqzlqrrt3o">
          <div className="mx-auto max-w-xl rounded-3xl bg-white p-7 text-center shadow-2xl sm:p-8" data-id="element-103" data-mp="12smim39rr097">
            <SparklesIcon className="mx-auto h-9 w-9 text-brand-medium" aria-hidden="true" data-id="element-104" data-mp="ys3t4fvcm6a6" />
            <h2 className="mt-4 font-display text-3xl font-bold text-brand-deep sm:text-4xl" data-id="element-105" data-mp="1crvc6v0q2cv1">
              Everything You Get Today
            </h2>
            <p className="mt-2 font-display text-2xl font-bold text-brand-deep sm:text-3xl" data-id="element-106" data-mp="17t1v6sm883q4">
              Astrologer Ready
            </p>
            <ul className="mt-7 space-y-3 text-left" role="list" data-id="element-107" data-mp="f3v0k4pw7ya5">
              {offerChecklist.map(item => <li key={item} className="flex items-start gap-3 text-[15px] leading-snug text-slate-700" data-id="element-108" data-mp="ts7l85jj5upc">
                  <CheckIcon className="mt-0.5 h-5 w-5 flex-none text-brand-medium" aria-hidden="true" data-id="element-109" data-mp="p20w84l3w86k" />
                  <span data-id="element-110" data-mp="1bn30aqsc3ae">{item}</span>
                </li>)}
            </ul>
            <div className="mt-8 flex flex-col items-center border-t border-brand-medium/15 pt-7 text-center" data-id="element-111" data-mp="19tzv5fds175">
              <p className="text-base font-bold text-slate-500 line-through" data-id="element-112" data-mp="280p63bl15gvh">99 USD</p>
              <p className="mt-1 font-display text-5xl font-bold leading-none text-brand-deep" data-id="element-113" data-mp="2cr5icsd46wg9">
                19 <span className="align-middle text-2xl" data-id="element-114" data-mp="1yzqzclj7iv97">USD</span>
              </p>
              <p className="mt-2 text-sm font-bold text-slate-600" data-id="element-115" data-mp="1yxo0rd3zntsv">
                One-time payment — Lifetime access
              </p>
            </div>
            <p className="mt-6 text-sm font-black text-brand-medium" data-id="element-116" data-mp="2qrzthirjrbim">
              Launch offer · Price goes up soon
            </p>
            <CtaButton label="YES, I WANT ASTROLOGER READY!" className="mt-3 w-full" target="checkout" data-id="element-117" data-mp="2vp8cflkirjkx" />
            <p className="mt-3 text-xs font-bold tracking-wide text-slate-500" data-id="element-118" data-mp="2hscqwtn2zznz">
              Secure checkout in your local currency
            </p>
            <p className="mt-2 text-xs font-bold tracking-wide text-slate-500" data-id="element-119" data-mp="16atsrfocymwa">
              🔒 Secure checkout <span aria-hidden="true" data-id="element-120" data-mp="1bag5lo09y14k">|</span> 📩 Instant delivery <span aria-hidden="true" data-id="element-121" data-mp="1b83fgnkd4kgf">|</span> 🛡️ 7-day guarantee
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="bg-brand-cream px-5 py-12 sm:py-16" data-id="element-122" data-mp="37zqzdh1auv5i">
          <div className="mx-auto max-w-3xl" data-id="element-123" data-mp="382lm5f7scs23">
            <div className="text-center" data-id="element-124" data-mp="338hd8qeym9yd">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-125" data-mp="33767il1yes4m">
                Clear answers
              </p>
              <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-126" data-mp="2u94aeglccjmm">
                Frequently Asked Questions
              </h2>
            </div>
            <div className="mt-8 space-y-3" data-id="element-127" data-mp="2ka51qy0ksnzm">
              {faqs.map(faq => <FaqItem key={faq.question} {...faq} data-id="element-128" data-mp="2kcra2zvaj7iw" />)}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section id="final-cta" className="bg-brand-deep px-5 py-12 text-center text-white sm:py-16" data-id="element-129" data-mp="ijtl5f4mwz38">
          <div className="mx-auto max-w-3xl" data-id="element-130" data-mp="eh6qz7khr22v">
            <ZapIcon className="mx-auto h-10 w-10 text-brand-gold" aria-hidden="true" data-id="element-131" data-mp="eihr8xeq9rp8" />
            <h2 className="mt-5 font-display text-4xl font-bold tracking-tight sm:text-5xl" data-id="element-132" data-mp="1wetkteiu9u2s">
              Stop improvising. Work with a system.
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-white/85" data-id="element-133" data-mp="264mm9mp9ostb">
              If you implement the system and it does not cut your prep time or improve the quality of your consultations, we refund 100% of your investment. No questions. No hassle.
            </p>
            <p className="mt-5 text-sm font-black text-brand-gold" data-id="element-134" data-mp="1ifacakm48gpy">
              Now with the complete 6×1 system!
            </p>
            <CtaButton label="YES, I WANT ASTROLOGER READY!" className="mt-3" data-id="element-135" data-mp="1mg3ztcvnbtib" />
            <p className="mt-3 text-xs font-bold tracking-wide text-white/70" data-id="element-136" data-mp="1rgrty7f68qgr">
              Secure checkout in your local currency
            </p>
          </div>
        </section>

      </main>

      <footer className="bg-[#1D1233] px-5 py-10 text-center text-white/70" data-id="element-137" data-mp="32uvujwg9lcus">
        <ShieldCheckIcon className="mx-auto h-6 w-6 text-brand-gold" aria-hidden="true" data-id="element-138" data-mp="2eiuytpvu5zop" />
        <p className="mt-4 text-sm" data-id="element-139" data-mp="2egh4jj8zcri7">© 2026 Astrologer Ready by Prof. Kyra · profkyra@gmail.com</p>
        <nav aria-label="Footer navigation" className="mt-3 flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm" data-id="element-140" data-mp="29hxgndatt5n7">
          <a href="#privacy" className="underline-offset-4 hover:text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold" data-id="element-141" data-mp="2ikowdpaqinkn">
            Privacy Policy
          </a>
          <a href="#terms" className="underline-offset-4 hover:text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold" data-id="element-142" data-mp="2sai58ijhsol7">
            Terms & Conditions
          </a>
          <a href="mailto:profkyra@gmail.com" className="underline-offset-4 hover:text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold" data-id="element-143" data-mp="327wd3trco51w">
            Contact
          </a>
        </nav>
        <p className="mx-auto mt-6 max-w-3xl text-xs leading-relaxed text-white/55" data-id="element-144" data-mp="17vpz6n72na5w">
          This product is an educational and informational resource for astrologers. It does not constitute legal, financial, or medical advice.
        </p>
      </footer>

    </div>;
}